/**
 * LeadCaptureForm - Displays lead capture forms in 2D HTML UI
 *
 * Renders native forms within virtual tours for lead generation.
 * Supports email capture, contact forms, and custom inquiry forms.
 *
 * VR mode is handled separately by LeadCaptureFormVR.
 */

import { Scene } from '@babylonjs/core/scene';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { HotspotConfig } from '../types/config';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('LeadCaptureForm');

import type {
  LeadFormConfig,
  FormField,
  FormSubmissionResult,
  FormValidationResult,
  FieldValidationError
} from '../types/forms';

/**
 * Request payload for lead form submission
 */
interface LeadFormRequest {
  sequenceId: string;
  stageId: string | null;
  hotspotId: string | undefined;
  formData: Record<string, string>;
  sessionId: string;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'vr';
}

/**
 * Callback for analytics tracking
 */
export interface LeadFormAnalyticsCallbacks {
  onFormViewed?: (sequenceId: string, hotspotId: string, formType: string) => void;
  onFormSubmitted?: (sequenceId: string, hotspotId: string, formType: string) => void;
  onFormAbandoned?: (sequenceId: string, hotspotId: string, formType: string) => void;
  onFormError?: (sequenceId: string, hotspotId: string, error: string) => void;
}

export class LeadCaptureForm {
  private sequenceId: string;
  private sessionId: string;

  // HTML UI elements
  private htmlPanel: HTMLElement | null = null;
  private currentHotspot: HotspotConfig | null = null;
  private currentStageId: string | null = null;

  // Form state
  private isSubmitting: boolean = false;
  private formStartTime: number = 0; // For analytics (time-to-submit)

  // Analytics callbacks
  private analytics: LeadFormAnalyticsCallbacks;

  // Honeypot field name (randomized to evade bots)
  private honeypotFieldName: string;

  constructor(
    scene: Scene,
    sequenceId: string,
    sessionId: string,
    analytics: LeadFormAnalyticsCallbacks = {}
  ) {
    // Scene stored for potential future VR integration
    void scene;
    this.sequenceId = sequenceId;
    this.sessionId = sessionId;
    this.analytics = analytics;
    this.honeypotFieldName = `_hp_${Math.random().toString(36).substring(7)}`;

    this.createHTMLPanel();
  }

  /**
   * Create the HTML panel container
   */
  private createHTMLPanel(): void {
    this.htmlPanel = document.createElement('div');
    this.htmlPanel.id = 'lead-capture-form-panel';
    this.htmlPanel.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1001;
      display: none;
      width: 90%;
      max-width: 450px;
      max-height: 90vh;
      overflow-y: auto;
      background: rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(40px);
      -webkit-backdrop-filter: blur(40px);
      border: 1px solid rgba(255, 255, 255, 0.25);
      border-radius: 32px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.1);
      padding: 32px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      animation: leadFormFadeIn 0.3s ease-out;
    `;

    // Add backdrop overlay
    const backdrop = document.createElement('div');
    backdrop.id = 'lead-capture-backdrop';
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1000;
      display: none;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    backdrop.onclick = () => this.handleBackdropClick();
    document.body.appendChild(backdrop);

    // Add to body
    document.body.appendChild(this.htmlPanel);

    // Add animation styles
    this.addStyles();
  }

  /**
   * Add CSS styles for the form
   */
  private addStyles(): void {
    if (document.getElementById('lead-form-styles')) return;

    const style = document.createElement('style');
    style.id = 'lead-form-styles';
    style.innerHTML = `
      @keyframes leadFormFadeIn {
        from { opacity: 0; transform: translate(-50%, -45%); }
        to { opacity: 1; transform: translate(-50%, -50%); }
      }

      .lead-form-input {
        width: 100%;
        padding: 16px 20px;
        font-size: 16px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 16px;
        background: rgba(255, 255, 255, 0.1);
        color: white;
        outline: none;
        transition: all 0.2s ease;
        box-sizing: border-box;
        font-family: inherit;
      }

      .lead-form-input::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }

      .lead-form-input:focus {
        border-color: rgba(255, 255, 255, 0.5);
        background: rgba(255, 255, 255, 0.15);
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
      }

      .lead-form-input.error {
        border-color: #ff6b6b;
        background: rgba(255, 107, 107, 0.1);
      }

      .lead-form-textarea {
        min-height: 100px;
        resize: vertical;
      }

      .lead-form-select {
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 16px center;
        padding-right: 40px;
      }

      .lead-form-select option {
        background: #1a1a1a;
        color: white;
      }

      .lead-form-checkbox-wrapper {
        display: flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;
      }

      .lead-form-checkbox {
        width: 24px;
        height: 24px;
        border-radius: 6px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        background: rgba(255, 255, 255, 0.1);
        cursor: pointer;
        appearance: none;
        position: relative;
      }

      .lead-form-checkbox:checked {
        background: white;
        border-color: white;
      }

      .lead-form-checkbox:checked::after {
        content: '\\2713';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: black;
        font-size: 14px;
        font-weight: bold;
      }

      .lead-form-label {
        display: block;
        margin-bottom: 8px;
        font-size: 14px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.9);
      }

      .lead-form-label .required {
        color: #ff6b6b;
        margin-left: 4px;
      }

      .lead-form-error {
        color: #ff6b6b;
        font-size: 12px;
        margin-top: 6px;
        display: none;
      }

      .lead-form-error.visible {
        display: block;
      }

      .lead-form-field {
        margin-bottom: 20px;
      }

      .lead-form-submit {
        width: 100%;
        padding: 18px;
        font-size: 18px;
        font-weight: 600;
        border: none;
        border-radius: 16px;
        background: white;
        color: #1a1a1a;
        cursor: pointer;
        transition: all 0.2s ease;
        margin-top: 8px;
      }

      .lead-form-submit:hover:not(:disabled) {
        transform: scale(1.02);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
      }

      .lead-form-submit:active:not(:disabled) {
        transform: scale(0.98);
      }

      .lead-form-submit:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .lead-form-submit.loading {
        position: relative;
        color: transparent;
      }

      .lead-form-submit.loading::after {
        content: '';
        position: absolute;
        width: 24px;
        height: 24px;
        top: 50%;
        left: 50%;
        margin-top: -12px;
        margin-left: -12px;
        border: 3px solid #1a1a1a;
        border-radius: 50%;
        border-top-color: transparent;
        animation: leadFormSpinner 0.8s linear infinite;
      }

      @keyframes leadFormSpinner {
        to { transform: rotate(360deg); }
      }

      .lead-form-success {
        text-align: center;
        padding: 40px 20px;
      }

      .lead-form-success-icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 24px;
        background: rgba(76, 217, 100, 0.2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 40px;
        animation: leadFormSuccessPop 0.4s ease;
      }

      @keyframes leadFormSuccessPop {
        0% { transform: scale(0); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }

      .lead-form-success-message {
        font-size: 18px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.9);
      }

      /* Honeypot - hidden from users but visible to bots */
      .lead-form-hp {
        position: absolute;
        left: -9999px;
        opacity: 0;
        height: 0;
        width: 0;
        z-index: -1;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Show the lead capture form for a hotspot
   */
  show(hotspot: HotspotConfig, stageId: string): void {
    if (!hotspot.leadForm?.enabled) {
      logger.warn('Form not enabled for this hotspot');
      return;
    }

    this.currentHotspot = hotspot;
    this.currentStageId = stageId;
    this.formStartTime = Date.now();
    this.isSubmitting = false;

    this.renderForm(hotspot.leadForm);

    // Show backdrop and panel
    const backdrop = document.getElementById('lead-capture-backdrop');
    if (backdrop) {
      backdrop.style.display = 'block';
      requestAnimationFrame(() => {
        backdrop.style.opacity = '1';
      });
    }

    if (this.htmlPanel) {
      this.htmlPanel.style.display = 'block';
    }

    // Track form view
    this.analytics.onFormViewed?.(
      this.sequenceId,
      hotspot.id,
      hotspot.leadForm.formType
    );
  }

  /**
   * Render the form content
   */
  private renderForm(config: LeadFormConfig): void {
    if (!this.htmlPanel) return;

    this.htmlPanel.innerHTML = '';

    // Close button (if dismissible)
    if (config.allowDismiss !== false) {
      const closeBtn = this.createCloseButton();
      this.htmlPanel.appendChild(closeBtn);
    }

    // Title
    if (config.title) {
      const title = document.createElement('h2');
      title.textContent = config.title;
      title.style.cssText = `
        margin: 0 0 8px 0;
        font-size: 28px;
        font-weight: 700;
        color: white;
        letter-spacing: -0.5px;
        padding-right: 40px;
      `;
      this.htmlPanel.appendChild(title);
    }

    // Description
    if (config.description) {
      const description = document.createElement('p');
      description.textContent = config.description;
      description.style.cssText = `
        margin: 0 0 24px 0;
        font-size: 16px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.7);
      `;
      this.htmlPanel.appendChild(description);
    }

    // Form element
    const form = document.createElement('form');
    form.id = 'lead-capture-form';
    form.noValidate = true;
    form.onsubmit = (e) => this.handleSubmit(e);

    // Honeypot field (spam protection)
    if (config.honeypotEnabled !== false) {
      const honeypot = document.createElement('input');
      honeypot.type = 'text';
      honeypot.name = this.honeypotFieldName;
      honeypot.tabIndex = -1;
      honeypot.autocomplete = 'off';
      honeypot.className = 'lead-form-hp';
      form.appendChild(honeypot);
    }

    // Form fields
    config.fields.forEach(field => {
      const fieldElement = this.createFieldElement(field);
      form.appendChild(fieldElement);
    });

    // Submit button
    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.className = 'lead-form-submit';
    submitBtn.textContent = config.submitButtonText || 'Submit';
    form.appendChild(submitBtn);

    this.htmlPanel.appendChild(form);
  }

  /**
   * Create a form field element
   */
  private createFieldElement(field: FormField): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'lead-form-field';
    wrapper.dataset.fieldId = field.id;

    // Hidden fields
    if (field.type === 'hidden') {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = field.name;
      input.value = field.defaultValue || '';
      wrapper.appendChild(input);
      return wrapper;
    }

    // Label
    const label = document.createElement('label');
    label.className = 'lead-form-label';
    label.htmlFor = `field-${field.id}`;
    label.textContent = field.label;
    if (field.required) {
      const requiredSpan = document.createElement('span');
      requiredSpan.className = 'required';
      requiredSpan.textContent = '*';
      label.appendChild(requiredSpan);
    }
    wrapper.appendChild(label);

    // Input element
    let input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    if (field.type === 'textarea') {
      input = document.createElement('textarea');
      input.className = 'lead-form-input lead-form-textarea';
    } else if (field.type === 'select') {
      input = document.createElement('select');
      input.className = 'lead-form-input lead-form-select';

      // Add placeholder option
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = field.placeholder || 'Select an option';
      placeholder.disabled = true;
      placeholder.selected = true;
      input.appendChild(placeholder);

      // Add options
      field.options?.forEach(opt => {
        const option = document.createElement('option');
        option.value = opt.value;
        option.textContent = opt.label;
        input.appendChild(option);
      });
    } else if (field.type === 'checkbox') {
      const checkWrapper = document.createElement('label');
      checkWrapper.className = 'lead-form-checkbox-wrapper';

      input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'lead-form-checkbox';

      const checkLabel = document.createElement('span');
      checkLabel.textContent = field.placeholder || '';

      checkWrapper.appendChild(input);
      checkWrapper.appendChild(checkLabel);
      wrapper.appendChild(checkWrapper);
    } else {
      input = document.createElement('input');
      input.type = field.type;
      input.className = 'lead-form-input';
    }

    input.id = `field-${field.id}`;
    input.name = field.name;

    if (field.type !== 'checkbox' && field.type !== 'select') {
      (input as HTMLInputElement).placeholder = field.placeholder || '';
    }

    if (field.defaultValue) {
      if (field.type === 'checkbox') {
        (input as HTMLInputElement).checked = field.defaultValue === 'true';
      } else {
        input.value = field.defaultValue;
      }
    }

    if (field.maxLength && 'maxLength' in input) {
      (input as HTMLInputElement | HTMLTextAreaElement).maxLength = field.maxLength;
    }

    if (field.pattern) {
      (input as HTMLInputElement).pattern = field.pattern;
    }

    // Real-time validation
    input.onblur = () => this.validateField(field, input);
    input.oninput = () => {
      // Clear error on input
      const errorEl = wrapper.querySelector('.lead-form-error');
      if (errorEl) {
        errorEl.classList.remove('visible');
        input.classList.remove('error');
      }
    };

    if (field.type !== 'checkbox') {
      wrapper.appendChild(input);
    }

    // Error message container
    const error = document.createElement('div');
    error.className = 'lead-form-error';
    error.id = `error-${field.id}`;
    wrapper.appendChild(error);

    return wrapper;
  }

  /**
   * Create close button
   */
  private createCloseButton(): HTMLElement {
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: white;
      font-size: 28px;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      backdrop-filter: blur(10px);
    `;

    closeBtn.onmouseenter = () => {
      closeBtn.style.background = 'rgba(255, 255, 255, 0.25)';
      closeBtn.style.transform = 'scale(1.1)';
    };
    closeBtn.onmouseleave = () => {
      closeBtn.style.background = 'rgba(255, 255, 255, 0.1)';
      closeBtn.style.transform = 'scale(1)';
    };
    closeBtn.onclick = () => this.hide(true);

    return closeBtn;
  }

  /**
   * Validate a single field
   */
  private validateField(field: FormField, input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): boolean {
    const wrapper = input.closest('.lead-form-field');
    const errorEl = wrapper?.querySelector('.lead-form-error');

    let isValid = true;
    let errorMessage = '';

    const value = field.type === 'checkbox'
      ? (input as HTMLInputElement).checked ? 'true' : ''
      : input.value.trim();

    // Required check
    if (field.required && !value) {
      isValid = false;
      errorMessage = field.errorMessage || `${field.label} is required`;
    }

    // Email validation
    if (isValid && field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        isValid = false;
        errorMessage = 'Please enter a valid email address';
      }
    }

    // Phone validation
    if (isValid && field.type === 'tel' && value) {
      const phoneRegex = /^[\d\s\-+()]{7,}$/;
      if (!phoneRegex.test(value)) {
        isValid = false;
        errorMessage = 'Please enter a valid phone number';
      }
    }

    // Pattern validation
    if (isValid && field.pattern && value) {
      const regex = new RegExp(field.pattern);
      if (!regex.test(value)) {
        isValid = false;
        errorMessage = field.errorMessage || 'Invalid format';
      }
    }

    // Update UI
    if (!isValid && errorEl) {
      errorEl.textContent = errorMessage;
      errorEl.classList.add('visible');
      input.classList.add('error');
    } else if (errorEl) {
      errorEl.classList.remove('visible');
      input.classList.remove('error');
    }

    return isValid;
  }

  /**
   * Validate entire form
   */
  private validateForm(): FormValidationResult {
    const errors: FieldValidationError[] = [];
    const form = document.getElementById('lead-capture-form') as HTMLFormElement;
    if (!form || !this.currentHotspot?.leadForm) return { isValid: false, errors: [] };

    const config = this.currentHotspot.leadForm;

    config.fields.forEach(field => {
      if (field.type === 'hidden') return;

      const input = form.querySelector(`[name="${field.name}"]`) as HTMLInputElement;
      if (!input) return;

      const isValid = this.validateField(field, input);
      if (!isValid) {
        errors.push({
          fieldId: field.id,
          fieldName: field.name,
          message: field.errorMessage || `${field.label} is invalid`
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Handle form submission
   */
  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    if (this.isSubmitting || !this.currentHotspot?.leadForm) return;

    const form = e.target as HTMLFormElement;
    const submitBtn = form.querySelector('.lead-form-submit') as HTMLButtonElement;

    // Check honeypot
    const honeypotValue = (form.querySelector(`[name="${this.honeypotFieldName}"]`) as HTMLInputElement)?.value;
    if (honeypotValue) {
      logger.warn('Honeypot triggered, likely bot submission');
      // Fake success to confuse bots
      this.showSuccess(this.currentHotspot.leadForm.successMessage);
      return;
    }

    // Validate
    const validation = this.validateForm();
    if (!validation.isValid) {
      // Focus first error field
      const firstError = validation.errors[0];
      const firstInput = form.querySelector(`[name="${firstError.fieldName}"]`) as HTMLElement;
      firstInput?.focus();
      return;
    }

    // Calculate time-to-submit for analytics
    const timeToSubmit = Date.now() - this.formStartTime;
    logger.debug(`Time to submit: ${timeToSubmit}ms`);

    // Collect form data
    const formData: Record<string, string> = {};
    this.currentHotspot.leadForm.fields.forEach(field => {
      const input = form.querySelector(`[name="${field.name}"]`) as HTMLInputElement;
      if (!input) return;

      if (field.type === 'checkbox') {
        formData[field.name] = input.checked ? 'Yes' : 'No';
      } else {
        formData[field.name] = input.value.trim();
      }
    });

    // Start submission
    this.isSubmitting = true;
    submitBtn.disabled = true;
    submitBtn.classList.add('loading');

    try {
      const result = await this.submitToBackend(formData);

      if (result.success) {
        // Track success
        this.analytics.onFormSubmitted?.(
          this.sequenceId,
          this.currentHotspot.id,
          this.currentHotspot.leadForm.formType
        );

        // Show success
        this.showSuccess(result.message || this.currentHotspot.leadForm.successMessage);

        // Handle redirect
        if (result.redirectUrl) {
          setTimeout(() => {
            window.open(result.redirectUrl, '_blank');
          }, 1500);
        }
      } else {
        this.showError(result.message || 'Submission failed. Please try again.');
        this.analytics.onFormError?.(
          this.sequenceId,
          this.currentHotspot.id,
          result.message || 'Unknown error'
        );
      }
    } catch (error) {
      logger.error('Submission error', error);
      this.showError('An error occurred. Please try again.');
      const errorMessage = error instanceof Error ? error.message : 'Submission exception';
      this.analytics.onFormError?.(
        this.sequenceId,
        this.currentHotspot.id,
        errorMessage
      );
    } finally {
      this.isSubmitting = false;
      submitBtn.disabled = false;
      submitBtn.classList.remove('loading');
    }
  }

  /**
   * Submit form data to backend
   */
  private async submitToBackend(formData: Record<string, string>): Promise<FormSubmissionResult> {
    const functions = getFunctions();
    const submitLeadForm = httpsCallable<LeadFormRequest, FormSubmissionResult>(functions, 'submitLeadForm');

    const result = await submitLeadForm({
      sequenceId: this.sequenceId,
      stageId: this.currentStageId,
      hotspotId: this.currentHotspot?.id,
      formData,
      sessionId: this.sessionId,
      deviceType: this.getDeviceType()
    });

    return result.data;
  }

  /**
   * Get current device type
   */
  private getDeviceType(): 'desktop' | 'mobile' | 'tablet' | 'vr' {
    const ua = navigator.userAgent.toLowerCase();
    if (/tablet|ipad|playbook|silk/.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/.test(ua)) return 'mobile';
    return 'desktop';
  }

  /**
   * Show success state
   */
  private showSuccess(message: string): void {
    if (!this.htmlPanel) return;

    // Clear existing content
    this.htmlPanel.textContent = '';

    // Build success UI using safe DOM methods (prevents XSS)
    const container = document.createElement('div');
    container.className = 'lead-form-success';

    const icon = document.createElement('div');
    icon.className = 'lead-form-success-icon';
    icon.textContent = '\u2713'; // Unicode checkmark

    const messageEl = document.createElement('div');
    messageEl.className = 'lead-form-success-message';
    messageEl.textContent = message; // Safe: textContent escapes HTML

    container.appendChild(icon);
    container.appendChild(messageEl);
    this.htmlPanel.appendChild(container);

    // Auto-close after delay
    setTimeout(() => {
      this.hide();
    }, 3000);
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    // Show inline error at top of form
    const form = document.getElementById('lead-capture-form');
    if (!form) return;

    let errorBanner = form.querySelector('.lead-form-error-banner') as HTMLElement;
    if (!errorBanner) {
      errorBanner = document.createElement('div');
      errorBanner.className = 'lead-form-error-banner';
      errorBanner.style.cssText = `
        background: rgba(255, 107, 107, 0.2);
        border: 1px solid rgba(255, 107, 107, 0.5);
        border-radius: 12px;
        padding: 12px 16px;
        margin-bottom: 20px;
        font-size: 14px;
        color: #ff6b6b;
      `;
      form.insertBefore(errorBanner, form.firstChild);
    }

    errorBanner.textContent = message;
  }

  /**
   * Handle backdrop click
   */
  private handleBackdropClick(): void {
    const config = this.currentHotspot?.leadForm;
    if (config?.allowDismiss !== false) {
      this.hide(true);
    }
  }

  /**
   * Hide the form
   */
  hide(wasAbandoned: boolean = false): void {
    // Track abandonment if form wasn't submitted
    if (wasAbandoned && this.currentHotspot?.leadForm && !this.isSubmitting) {
      this.analytics.onFormAbandoned?.(
        this.sequenceId,
        this.currentHotspot.id,
        this.currentHotspot.leadForm.formType
      );
    }

    // Hide backdrop
    const backdrop = document.getElementById('lead-capture-backdrop');
    if (backdrop) {
      backdrop.style.opacity = '0';
      setTimeout(() => {
        backdrop.style.display = 'none';
      }, 300);
    }

    // Hide panel
    if (this.htmlPanel) {
      this.htmlPanel.style.display = 'none';
    }

    this.currentHotspot = null;
    this.currentStageId = null;
  }

  /**
   * Check if form is currently visible
   */
  isVisible(): boolean {
    return this.htmlPanel?.style.display === 'block';
  }

  /**
   * Get current hotspot
   */
  getCurrentHotspot(): HotspotConfig | null {
    return this.currentHotspot;
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    // Remove backdrop
    const backdrop = document.getElementById('lead-capture-backdrop');
    backdrop?.remove();

    // Remove panel
    this.htmlPanel?.remove();
    this.htmlPanel = null;

    // Remove styles
    document.getElementById('lead-form-styles')?.remove();

    this.currentHotspot = null;
    this.currentStageId = null;
  }
}
